import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, CircularProgress, Divider, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { listPots, savePot, listCleaningRules, saveCleaningRule } from './api/tumblerApi';
import normalizeApiError from './utils/normalizeApiError';

// IMPLEMENTATION_PLAN.md Phase 5 — "Physical pot และ cleaning rule CRUD ใช้ API ที่มี; allergy
// group IDs กับ labels ต้องแยก" (ASSESSMENT.md P2: no UI existed for tumbler-master /
// tumbler-cleaning-rules even though the backend flow supports them). Allergen group is stored
// and displayed as its id, with the human label shown alongside it rather than collapsed into
// one field, since editing the label here should never silently rewrite which group id a rule
// points at.
export default function ResourceSettings({ plant, factory }) {
  const accessToken = useSelector((state) => state.auth.access_token);
  const generation = useRef(0);
  const controllerRef = useRef(null);
  const [pots, setPots] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [savingPotId, setSavingPotId] = useState(null);
  const [savingRuleId, setSavingRuleId] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken || !plant || !factory) return;
    const key = ++generation.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    try {
      const [potResponse, ruleResponse] = await Promise.all([
        listPots({ plant, factory, accessToken, signal: controller.signal }),
        listCleaningRules({ plant, factory, accessToken, signal: controller.signal }),
      ]);
      if (key !== generation.current) return;
      setPots(Array.isArray(potResponse.data) ? potResponse.data : potResponse.data.results || []);
      setRules(Array.isArray(ruleResponse.data) ? ruleResponse.data : ruleResponse.data.results || []);
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || key !== generation.current) return;
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to load pots / cleaning rules.').summary });
    } finally {
      if (key === generation.current) setLoading(false);
    }
    return () => controller.abort();
  }, [plant, factory, accessToken]);

  useEffect(() => { load(); const counter = generation; const controller = controllerRef.current; return () => { ++counter.current; controller?.abort(); }; }, [load]);

  const updatePot = (index, field, value) => {
    setPots((current) => current.map((pot, i) => (i === index ? { ...pot, [field]: value } : pot)));
  };

  const savePotRow = async (index) => {
    const pot = pots[index];
    setSavingPotId(pot.id ?? `new-${index}`);
    try {
      const payload = { ...pot, plant_id: plant, factory };
      const response = await savePot({ potId: pot.id, payload, accessToken });
      setPots((current) => current.map((p, i) => (i === index ? response.data : p)));
      setMessage({ severity: 'success', text: `Pot ${response.data.pot_id || response.data.id} saved.` });
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to save pot.').summary });
    } finally {
      setSavingPotId(null);
    }
  };

  const updateRule = (index, field, value) => {
    setRules((current) => current.map((rule, i) => (i === index ? { ...rule, [field]: value } : rule)));
  };

  const saveRuleRow = async (index) => {
    const rule = rules[index];
    setSavingRuleId(rule.id ?? `new-${index}`);
    try {
      const payload = { ...rule, plant_id: plant, factory };
      const response = await saveCleaningRule({ ruleId: rule.id, payload, accessToken });
      setRules((current) => current.map((r, i) => (i === index ? response.data : r)));
      setMessage({ severity: 'success', text: 'Cleaning rule saved.' });
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to save cleaning rule.').summary });
    } finally {
      setSavingRuleId(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Physical pots</Typography>
        <Tooltip title="Add pot">
          <IconButton onClick={() => setPots((current) => [...current, { pot_id: '', capacity_kg: '', is_active: true }])}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {pots.map((pot, index) => (
          <Stack key={pot.id ?? `new-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <TextField label="Pot ID" value={pot.pot_id ?? ''} onChange={(event) => updatePot(index, 'pot_id', event.target.value)} size="small" />
            <TextField label="Capacity (kg)" type="number" value={pot.capacity_kg ?? ''} onChange={(event) => updatePot(index, 'capacity_kg', event.target.value)} size="small" />
            <Button size="small" disabled={savingPotId === (pot.id ?? `new-${index}`)} onClick={() => savePotRow(index)}>
              {savingPotId === (pot.id ?? `new-${index}`) ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        ))}
        {!pots.length && <Typography color="text.secondary">No physical pots configured yet.</Typography>}
      </Stack>

      <Divider sx={{ my: 4 }} />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Cleaning rules</Typography>
        <Tooltip title="Add rule">
          <IconButton onClick={() => setRules((current) => [...current, { from_product_group: '', to_product_group: '', cleaning_duration_minutes: '' }])}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {rules.map((rule, index) => (
          <Stack key={rule.id ?? `new-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <TextField label="From group codes (e.g. 1,3)" value={rule.from_product_group ?? ''} onChange={(event) => updateRule(index, 'from_product_group', event.target.value)} size="small" />
            <TextField label="To group codes (e.g. 5)" value={rule.to_product_group ?? ''} onChange={(event) => updateRule(index, 'to_product_group', event.target.value)} size="small" />
            <TextField label="Cleaning time (min)" type="number" value={rule.cleaning_duration_minutes ?? ''} onChange={(event) => updateRule(index, 'cleaning_duration_minutes', event.target.value)} size="small" />
            <Button size="small" disabled={savingRuleId === (rule.id ?? `new-${index}`)} onClick={() => saveRuleRow(index)}>
              {savingRuleId === (rule.id ?? `new-${index}`) ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        ))}
        {!rules.length && <Typography color="text.secondary">No cleaning rules configured yet.</Typography>}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Use the comma-separated group codes from Product Master (e.g. 1,3), not database IDs.
      </Typography>
    </Box>
  );
}
